import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { StatesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as StateActions from "../../../actions/states";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { LookupsLayout } from "../../index";

import "./StatesPage.scss";

interface StatesPageProps {
    formFilter: IFormFilter;
    states: Dtos.State[];
    loadingStates: boolean;
    loadStatesSuccess: boolean;
    loadStatesFailure: boolean;
    loadStatesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearStates: StateActions.IStateClearActionCreator;
    loadStates: StateActions.IStateLoadActionCreator;
    filter: StateActions.IStateFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const StatesPage: FC<StatesPageProps> = ({
    formFilter,
    states,
    loadingStates,
    loadStatesSuccess,
    loadStatesFailure,
    loadStatesErrorCode,
    permissions,
    clearStates,
    loadStates,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showStates = !loadingStates && loadStatesSuccess;


    // Load states
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(StatesBreadcrumbs());

            loadStates();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        StatesBreadcrumbs,
        loadStates,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadStatesFailure) {
            navigate(`/error/${loadStatesErrorCode}`);
        }
    }, [loadStatesFailure, loadStatesErrorCode, navigate]);

    // Cleanup effect to clear the states on component unmount
    useEffect(() => {
        return () => {
            clearStates();
        };
    }, [clearStates]);

    const createTable = useMemo(() => {
        if (!states || states.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no states to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = states.length > DEFAULT_PAGE_SIZE;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={states}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? DEFAULT_PAGE_SIZE : states.length
                    }
                    pageSize={showPagination ? undefined : states.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no states to display"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url: string = `/settings/general/states/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [states, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, navigate]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>States</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.StatesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/general/states/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showStates ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showStates ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showStates, createTable]);

    return (
        <LookupsLayout
            loading={loadingStates}
            permission={Dtos.Permission.StatesView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<StatesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.states.formFilter,
        states: state.states.formFilteredData,
        loadingStates:
            state.states.loadState &&
            state.states.loadState.status == RequestState.Pending,
        loadStatesSuccess:
            state.states.loadState &&
            state.states.loadState.status == RequestState.Success,
        loadStatesFailure:
            state.states.loadState &&
            state.states.loadState.status == RequestState.Failure,
        loadStatesErrorCode: state.states.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<StatesPageProps> => {
    return {
        loadStates: bindActionCreators(StateActions.LoadState, dispatch),
        clearStates: bindActionCreators(StateActions.Clear, dispatch),
        filter: bindActionCreators(StateActions.Filter, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(StatesPage);
